import { useCallback, useMemo } from 'react';

/* O recorte de trabalho de uma listagem — busca, filtros, aba, ordem e página.

   Vive no `sessionStorage`: sobrevive a abrir um registro e voltar, a trocar de
   tela pela barra lateral e a um F5, e morre quando a aba do navegador fecha.
   É estado de trabalho, não dado — por isso não vai para o servidor, e perder
   não machuca ninguém.

   O padrão já rodava em três telas (Pipeline, Disparador e Templates) escrito à
   mão em cada uma. Isto aqui é aquele mesmo código, uma vez só.

   Uso:

     const [inicial, gravarRecorte] = useRecorte('ct:recorte', RECORTE_VAZIO);
     const [busca, setBusca] = useState(inicial.busca);
     const [origem, setOrigem] = useState(inicial.origem);
     useEffect(() => { gravarRecorte({ busca, origem }); }, [busca, origem, gravarRecorte]);

   Por que devolver o valor inicial em vez de controlar o estado inteiro: cada
   tela já tem um `useState` por filtro, e um `setBusca` espalhado por dez
   pontos do JSX. Trocar todos por um redutor mexeria em código que funciona
   para o meu encaixar. Assim o padrão entra em cinco linhas por listagem. */

export function useRecorte<T extends object>(chave: string, vazio: T) {
  /* Lido UMA vez, na montagem. Depois disso quem manda é o estado da tela — se
     isto reagisse ao storage, duas abas brigariam pelo mesmo filtro. */
  const inicial = useMemo<T>(() => {
    try {
      const cru = sessionStorage.getItem(chave);
      if (!cru) return vazio;
      const lido = JSON.parse(cru) as Partial<T> | null;
      /* Espalhado por cima do vazio de propósito: um recorte gravado por uma
         versão anterior da tela pode não ter um campo que hoje existe, e o
         `undefined` viraria um filtro sem valor em vez do padrão. */
      return lido && typeof lido === 'object' ? { ...vazio, ...lido } : vazio;
    } catch {
      return vazio;
    }
    /* `vazio` chega como literal na chamada: entra nas dependências e o memo
       nunca acertaria. A chave é o que identifica o recorte. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const gravar = useCallback((recorte: T) => {
    try {
      sessionStorage.setItem(chave, JSON.stringify(recorte));
    } catch {
      /* Modo privado, cota estourada: é estado de trabalho, segue sem ele. */
    }
  }, [chave]);

  return [inicial, gravar] as const;
}
